import { camera, cameraOutline, folderOutline, videocamOutline } from 'ionicons/icons';
import { CameraResultType, CameraSource, Camera } from '@auto-it-australia/capacitor-camera';
import { IonButton, IonCol, IonContent, IonGrid, IonIcon, IonItem, IonItemDivider, IonRow, useIonAlert } from '@ionic/react';
import { CaptureVideoOptions, MediaFile, MediaCapture } from '@awesome-cordova-plugins/media-capture';

import { IBlobWithAttributes, MediaType } from '../../models/dms/attachment.model';
import { logTransaction } from '../../common/log';
import { LogType } from '../../models/log.model';
import { systemPathToBlob, webPathToBlob } from '../../common/util';
import store from '../../store';
import { PopoverWrapper, Wrapper } from './MediaCaptureButton.styles';
import { useAppSelector } from '../../store/hooks';
import { useState } from 'react';
import { useToast } from '../../providers/ToastProvider';
import { Color, PositionSide } from '@ionic/core';

interface IComponentProps {
  isLoading?: boolean;
  color?: (Color & string) | undefined;
  popoverSide?: PositionSide | undefined;
  onMediaCapture: (blob: IBlobWithAttributes, type: MediaType) => void;
}

enum CaptureType {
  CameraPhoto,
  CameraVideo,
  LibraryPhoto,
  LibraryVideo,
}

const MediaCaptureButton: React.FC<IComponentProps> = ({ isLoading, color = 'primary', popoverSide = 'bottom', onMediaCapture }) => {
  const [videoAllowed] = useState(false);

  const { mediaSettingsSync } = useAppSelector((state) => state.mediaSettingsSync);
  const [presentAlert] = useIonAlert();
  const {
    actions: { infoToast },
  } = useToast();

  const captureMedia = async (source: CaptureType) => {
    try {
      logTransaction(LogType.Info, `Capture photo requested with source: ${source}`);

      if ((source === CaptureType.CameraVideo || source === CaptureType.LibraryVideo) && !store.getState().mediaSettingsSync.mediaSettingsSync?.data.allowVideo) {
        await presentAlert({
          header: 'Video Capture Disabled',
          message: 'Video capture is disabled. Please contact Auto-IT support for information on enabling this feature.',
          buttons: ['OK'],
        });

        return;
      }

      switch (source) {
        case CaptureType.CameraPhoto:
          logTransaction(LogType.Info, `Capture photo requested`);
          const cameraPhoto = await Camera.getPhoto({
            source: CameraSource.Camera,
            quality: 90,
            allowEditing: false,
            resultType: CameraResultType.Uri,
            saveToGallery: true,
          });

          if (cameraPhoto.webPath) {
            const blob = await webPathToBlob(cameraPhoto.webPath, cameraPhoto.path, cameraPhoto.webPath);

            if (blob) {
              onMediaCapture(blob, MediaType.Image);
            } else {
              throw new Error('No blob found');
            }
          } else {
            throw new Error('No webPath found in photo');
          }
          break;
        case CaptureType.CameraVideo:
          logTransaction(LogType.Info, `Capture video requested`);
          const options = { limit: 1, duration: 30, quality: 0.5 } as CaptureVideoOptions;
          logTransaction(LogType.Info, `User chose to capture video: ${JSON.stringify(options)}`);
          const data = await MediaCapture.captureVideo(options);
          const cameraVideo = (data as MediaFile[])[0];
          logTransaction(LogType.Info, `Video captured: ${JSON.stringify(cameraVideo)}`);

          // Convert MediaFile URI to Blob
          logTransaction(LogType.Info, `Fetching file from URI: ${cameraVideo.fullPath}`);
          const blob = await systemPathToBlob(cameraVideo.fullPath, cameraVideo.type, cameraVideo.fullPath, undefined);

          if (blob) {
            onMediaCapture(blob, MediaType.Video);
          } else {
            throw new Error('No blob found');
          }
          break;
        case CaptureType.LibraryPhoto:
          logTransaction(LogType.Info, `Select photo from library requested`);
          const libraryPhoto = await Camera.getPhoto({
            source: CameraSource.Photos,
            quality: 90,
            allowEditing: false,
            resultType: CameraResultType.Uri,
          });

          if (libraryPhoto.webPath) {
            const blob = await webPathToBlob(libraryPhoto.webPath, libraryPhoto.path, libraryPhoto.webPath);

            if (blob) {
              onMediaCapture(blob, MediaType.Image);
            } else {
              throw new Error('No blob found');
            }
          } else {
            throw new Error('No webPath found in photo');
          }
          break;
        case CaptureType.LibraryVideo:
          logTransaction(LogType.Info, `Select video from library requested`);
          const libraryVideo = await Camera.getPhoto({
            source: CameraSource.Videos,
            resultType: CameraResultType.Uri,
          });

          if (libraryVideo.webPath) {
            const blob = await webPathToBlob(libraryVideo.webPath, libraryVideo.path, libraryVideo.webPath);

            if (blob) {
              onMediaCapture(blob, MediaType.Video);
            } else {
              throw new Error('No blob found');
            }
          } else {
            throw new Error('No webPath found in photo');
          }
          break;
      }

      infoToast('Media captured successfully');
    } catch (err) {
      logTransaction(LogType.Warn, `Error capturing photo - error: ${err}`);
    }
  };

  return (
    <Wrapper className="media-capture-button">
      <IonButton id="open-popover" className="button-take-photo" size="large" fill="outline" color={color} disabled={isLoading}>
        <IonIcon icon={camera} className="button-icon" />
      </IonButton>

      <PopoverWrapper trigger="open-popover" mode="ios" side={popoverSide} keepContentsMounted>
        <IonContent color="lightest">
          <IonGrid className="ion-padding">
            <IonItemDivider className="media-divider photos">Photo Capture</IonItemDivider>
            <IonRow>
              <IonCol size="6">
                <IonItem mode="md" button detail={false} lines="none" className="action-button" onClick={() => captureMedia(CaptureType.CameraPhoto)}>
                  <IonIcon icon={cameraOutline} className="button-icon" />
                </IonItem>
              </IonCol>
              <IonCol size="6">
                <IonItem mode="md" button detail={false} lines="none" className="action-button" onClick={() => captureMedia(CaptureType.LibraryPhoto)}>
                  <IonIcon icon={folderOutline} className="button-icon" />
                </IonItem>
              </IonCol>
            </IonRow>
            {videoAllowed && (
              <>
                <IonItemDivider className="media-divider videos">Video Capture</IonItemDivider>
                <IonRow>
                  <IonCol size="6">
                    <IonItem mode="md" button detail={false} lines="none" className={`action-button ${mediaSettingsSync?.data.allowVideo ? '' : 'disabled'}`} onClick={() => captureMedia(CaptureType.CameraVideo)}>
                      <IonIcon icon={videocamOutline} className={`button-icon ${mediaSettingsSync?.data.allowVideo ? '' : 'disabled'}`} />
                    </IonItem>
                  </IonCol>
                  <IonCol size="6">
                    <IonItem mode="md" button detail={false} lines="none" className={`action-button ${mediaSettingsSync?.data.allowVideo ? '' : 'disabled'}`} onClick={() => captureMedia(CaptureType.LibraryVideo)}>
                      <IonIcon icon={folderOutline} className={`button-icon ${mediaSettingsSync?.data.allowVideo ? '' : 'disabled'}`} />
                    </IonItem>
                  </IonCol>
                </IonRow>
              </>
            )}
          </IonGrid>
        </IonContent>
      </PopoverWrapper>
    </Wrapper>
  );
};

export default MediaCaptureButton;
